import { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

import { cn } from '../../../utils/cn';

const SearchBar = ({ value, onChange, onSearch, suggestions = [] }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e?.target?.value;
    onChange(newValue);
    setShowSuggestions(newValue?.length > 0);
    setHighlightedIndex(-1);
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion);
    setShowSuggestions(false);
    onSearch?.(suggestion);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions?.length === 0) return;

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setHighlightedIndex((prev) => 
          prev < suggestions?.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e?.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions?.length) {
          handleSuggestionClick(suggestions?.[highlightedIndex]);
        } else {
          onSearch?.(value);
          setShowSuggestions(false);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSearchClick = () => {
    onSearch?.(value);
    setShowSuggestions(false);
  };

  const handleClear = () => {
    onChange('');
    setShowSuggestions(false);
    onSearch?.('');
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <Icon 
          name="Search" 
          size={20} 
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
        />
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => value?.length > 0 && setShowSuggestions(true)}
          placeholder="Search jobs by title, skills, or keywords..."
          className="w-full h-12 pl-10 pr-20 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth rounded-br-xl rounded-t-xl rounded-bl-xl"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value && (
            <button
              onClick={handleClear}
              className="p-1.5 hover:bg-muted rounded-md transition-smooth"
              aria-label="Clear search"
            >
              <Icon name="X" size={16} className="text-muted-foreground" />
            </button>
          )}
          <button
            onClick={handleSearchClick}
            className="p-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth"
            aria-label="Search"
          >
            <Icon name="Search" size={16} />
          </button>
        </div>
      </div>

      {showSuggestions && suggestions?.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-2 bg-card border border-border rounded-md shadow-lg max-h-64 overflow-y-auto"
        >
          <div className="py-2">
            {suggestions?.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className={cn(
                  "w-full px-4 py-2.5 text-left text-sm hover:bg-muted transition-smooth flex items-center gap-2",
                  highlightedIndex === index && "bg-muted"
                )}
              >
                <Icon name="Search" size={14} className="text-muted-foreground flex-shrink-0" />
                <span className="text-foreground">{suggestion}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;